import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  RefreshCw,
  Package,
  Barcode,
  Hash,
  Layers,
  Building2,
  Ruler,
  Image as ImageIcon,
  Pencil,
  X,
  Save,
  Upload,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import useInventory from "../Services/InventoryServices/useInventory";

function GlobalInventory() {
  const { loading, updating, getMasterInventory, updateInventoryAndToast } =
    useInventory();

  const [inventory, setInventory] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [copiedKey, setCopiedKey] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [editForm, setEditForm] = useState({
    hsCode: "",
    imgUrl: "",
  });

  const cleanValue = (value, fallback = "-") => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text || fallback;
  };

  const fetchMasterInventory = useCallback(async () => {
    setError("");

    const result = await getMasterInventory();

    if (result.success) {
      setInventory(result.data || []);
      setCurrentPage(1);
    } else {
      setInventory([]);
      setError(result.message);
      toast.error(result.message);
    }
  }, [getMasterInventory]);

  useEffect(() => {
    fetchMasterInventory();
  }, [fetchMasterInventory]);

  const filteredInventory = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!query) return inventory;

    return inventory.filter((row) =>
      [
        row?.id,
        row?.barcode,
        row?.item,
        row?.hsn_code,
        row?.brand,
        row?.size,
        row?.img_url,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [inventory, searchText]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredInventory.length / pageSize));
  }, [filteredInventory.length, pageSize]);

  const paginatedInventory = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * pageSize;
    return filteredInventory.slice(start, start + pageSize);
  }, [filteredInventory, currentPage, pageSize, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const stats = useMemo(() => {
    return {
      total: inventory.length,
      withBarcode: inventory.filter(
        (item) => String(item?.barcode || "").trim() !== ""
      ).length,
      withBrand: inventory.filter(
        (item) => String(item?.brand || "").trim() !== ""
      ).length,
      withImage: inventory.filter(
        (item) => String(item?.img_url || "").trim() !== ""
      ).length,
    };
  }, [inventory]);

  const handleCopy = async ({ value, key, label }) => {
    const text = String(value || "").trim();

    if (!text) {
      toast.error(`${label} is empty`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(`${label} copied`);

      setTimeout(() => {
        setCopiedKey((prev) => (prev === key ? "" : prev));
      }, 1200);
    } catch (error) {
      console.error("Clipboard copy error:", error);
      toast.error("Unable to copy");
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setEditForm({
      hsCode: item?.hsn_code || "",
      imgUrl: item?.img_url || "",
    });
  };

  const closeEditModal = () => {
    if (updating) return;

    setSelectedItem(null);
    setEditForm({
      hsCode: "",
      imgUrl: "",
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      setEditForm((prev) => ({
        ...prev,
        imgUrl: reader.result,
      }));
    };

    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!selectedItem?.id) {
      toast.error("Inventory id missing");
      return;
    }

    const result = await updateInventoryAndToast({
      id: selectedItem.id,
      hsCode: editForm.hsCode,
      imgUrl: editForm.imgUrl,
      itemName: selectedItem?.item,
    });

    if (!result.success) return;

    setInventory((prev) =>
      prev.map((row) =>
        String(row?.id) === String(selectedItem?.id)
          ? {
              ...row,
              hsn_code: editForm.hsCode,
              img_url: result.imageUrl || editForm.imgUrl,
            }
          : row
      )
    );

    closeEditModal();
  };

  const goToFirstPage = () => setCurrentPage(1);

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const goToLastPage = () => setCurrentPage(totalPages);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        {/* Header */}
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
                <Package size={22} />
              </div>

              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Admin Inventory
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage master inventory, HSN code and item images.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={fetchMasterInventory}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#00ad70] disabled:cursor-not-allowed disabled:opacity-70"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={<Package size={19} />}
            label="Total Items"
            value={stats.total}
          />

          <StatCard
            icon={<Barcode size={19} />}
            label="With Barcode"
            value={stats.withBarcode}
          />

          <StatCard
            icon={<Building2 size={19} />}
            label="With Brand"
            value={stats.withBrand}
          />

          <StatCard
            icon={<ImageIcon size={19} />}
            label="With Image"
            value={stats.withImage}
          />
        </div>

        {/* Search + Page Size */}
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search
                size={17}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search item, barcode, HSN, brand..."
                className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-[#01C37D] focus:bg-white focus:ring-2 focus:ring-[#01C37D]/10"
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10"
              >
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
                <option value={200}>200 rows</option>
                <option value={500}>500 rows</option>
              </select>

              <div className="text-sm text-gray-500">
                Showing{" "}
                <span className="font-semibold text-gray-900">
                  {paginatedInventory.length}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-900">
                  {filteredInventory.length}
                </span>{" "}
                filtered items
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#01C37D] text-white">
                <tr>
                  <TableHead icon={<Hash size={15} />} label="ID" />
                  <TableHead icon={<ImageIcon size={15} />} label="Image" />
                  <TableHead icon={<Barcode size={15} />} label="Barcode" />
                  <TableHead icon={<Package size={15} />} label="Item" />
                  <TableHead icon={<Layers size={15} />} label="HSN Code" />
                  <TableHead icon={<Building2 size={15} />} label="Brand" />
                  <TableHead icon={<Ruler size={15} />} label="Size" />

                  <th className="whitespace-nowrap px-4 py-3 text-right font-semibold">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <TableLoader />
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="mx-auto max-w-sm">
                        <div className="text-sm font-medium text-red-600">
                          {error}
                        </div>

                        <button
                          type="button"
                          onClick={fetchMasterInventory}
                          className="mt-3 rounded-lg border px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Try again
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : filteredInventory.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="mx-auto max-w-sm">
                        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-gray-100 text-gray-400">
                          <Package size={22} />
                        </div>

                        <h3 className="mt-3 text-sm font-semibold text-gray-900">
                          No inventory found
                        </h3>

                        <p className="mt-1 text-sm text-gray-500">
                          Try changing your search keyword.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedInventory.map((row, index) => {
                    const rowId = row?.id || `${currentPage}-${index}`;
                    const barcodeCopyKey = `barcode-${rowId}`;
                    const itemCopyKey = `item-${rowId}`;

                    return (
                      <tr
                        key={`${row?.id || index}-${row?.barcode || index}`}
                        className="transition hover:bg-gray-50"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                          {cleanValue(row?.id)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          <InventoryImage src={row?.img_url} name={row?.item} />
                        </td>

                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-xs font-medium text-gray-700">
                              {cleanValue(row?.barcode)}
                            </span>

                            <CopyButton
                              copied={copiedKey === barcodeCopyKey}
                              onClick={() =>
                                handleCopy({
                                  value: row?.barcode,
                                  key: barcodeCopyKey,
                                  label: "Barcode",
                                })
                              }
                            />
                          </div>
                        </td>

                        <td className="min-w-[320px] px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="line-clamp-2 font-medium text-gray-900">
                              {cleanValue(row?.item)}
                            </div>

                            <CopyButton
                              copied={copiedKey === itemCopyKey}
                              onClick={() =>
                                handleCopy({
                                  value: row?.item,
                                  key: itemCopyKey,
                                  label: "Item name",
                                })
                              }
                            />
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                          {cleanValue(row?.hsn_code)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                          {cleanValue(row?.brand)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                          {cleanValue(row?.size)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 transition hover:border-[#01C37D] hover:bg-[#01C37D]/5 hover:text-[#01C37D]"
                          >
                            <Pencil size={14} />
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && filteredInventory.length > 0 && (
          <div className="flex flex-col items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3 shadow-sm sm:flex-row">
            <div className="text-sm text-gray-500">
              Page{" "}
              <span className="font-semibold text-gray-900">
                {currentPage}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-900">
                {totalPages}
              </span>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                First
              </button>

              <button
                type="button"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft size={16} />
                Prev
              </button>

              <button
                type="button"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
                <ChevronRight size={16} />
              </button>

              <button
                type="button"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedItem && (
        <EditInventoryModal
          selectedItem={selectedItem}
          editForm={editForm}
          setEditForm={setEditForm}
          updating={updating}
          onClose={closeEditModal}
          onSave={handleSave}
          onImageChange={handleImageChange}
        />
      )}
    </div>
  );
}

function EditInventoryModal({
  selectedItem,
  editForm,
  setEditForm,
  updating,
  onClose,
  onSave,
  onImageChange,
}) {
  const previewImage = editForm?.imgUrl;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Edit Inventory
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Update HSN code and item image.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={updating}
            className="grid h-9 w-9 place-items-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="rounded-xl border bg-gray-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Item
            </div>

            <div className="mt-1 font-semibold text-gray-900">
              {selectedItem?.item || "-"}
            </div>

            <div className="mt-2 text-xs text-gray-500">
              Barcode: {selectedItem?.barcode || "-"}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              HSN Code
            </label>

            <input
              value={editForm.hsCode}
              onChange={(e) =>
                setEditForm((prev) => ({
                  ...prev,
                  hsCode: e.target.value,
                }))
              }
              placeholder="Enter HSN code"
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#01C37D] focus:ring-2 focus:ring-[#01C37D]/10"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Item Image
            </label>

            <div className="flex flex-col gap-4 rounded-xl border border-dashed border-gray-300 p-4 sm:flex-row sm:items-center">
              <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-xl bg-gray-100">
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt={selectedItem?.item || "Inventory"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ImageIcon size={24} className="text-gray-400" />
                )}
              </div>

              <div className="flex-1">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-[#01C37D] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#00ad70]">
                  <Upload size={16} />
                  Choose Image

                  <input
                    type="file"
                    accept="image/*"
                    onChange={onImageChange}
                    className="hidden"
                  />
                </label>

                <p className="mt-2 text-xs text-gray-500">
                  Image will upload first, then the generated URL will be saved.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={updating}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onSave}
            disabled={updating}
            className="inline-flex items-center gap-2 rounded-xl bg-[#01C37D] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#00ad70] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {updating ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}

            {updating ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyButton({ copied, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={copied ? "Copied" : "Copy"}
      className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border text-xs transition ${
        copied
          ? "border-[#01C37D] bg-[#01C37D]/10 text-[#01C37D]"
          : "border-gray-200 bg-white text-gray-500 hover:border-[#01C37D] hover:bg-[#01C37D]/5 hover:text-[#01C37D]"
      }`}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

function InventoryImage({ src, name }) {
  if (!src) {
    return (
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-gray-100 text-gray-400">
        <ImageIcon size={18} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name || "Inventory"}
      className="h-11 w-11 rounded-xl border object-cover"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}

function TableHead({ icon, label }) {
  return (
    <th className="whitespace-nowrap px-4 py-3 font-semibold">
      <div className="flex items-center gap-2">
        {icon}
        {label}
      </div>
    </th>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
          {icon}
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {label}
          </div>

          <div className="mt-1 text-xl font-semibold text-gray-900">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function TableLoader() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <tr key={index}>
          {Array.from({ length: 8 }).map((__, cellIndex) => (
            <td key={cellIndex} className="px-4 py-3">
              <div className="h-4 w-full max-w-[180px] animate-pulse rounded bg-gray-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default GlobalInventory;